import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory31() {
  const { lesson, course, accent } = findLesson("music-theory", "31");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT31-Roman-Numerals-Animated.mp4"
        caption="Roman numeral analysis — same shape, any key"
        hint="≈ 30 sec · watch first"
      />
      <Chapter number="31" eyebrow="Music Theory · Chapter Thirty-One" title="Roman numeral analysis">
        <p>
          Chord charts tell you the <strong>absolute</strong> name of a chord — F major,
          G minor, B♭ major. <strong>Roman numerals</strong> tell you its
          <em> relative</em> name: where it sits inside the key. Once you can describe a
          progression as I → IV → V → I, you've severed the chord names from the key
          itself — and transposing the song to any other key becomes a five-second job.
        </p>
        <p>
          That portability is why Roman numerals are the lingua franca of session
          musicians, arrangers, classical analysts and jazz teachers worldwide. A singer
          changes key on the morning of a session because of a sore throat; the band
          shifts the chart to the new key in real time because the Roman numerals don't
          care which key they're in. They just describe the function.
        </p>
        <MTQuote>
          If you're working in studios, if you play with a lot of bands, if you session a
          lot, if you compose, produce, learning Roman numerals is an essential skill.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/31a-roman-numerals-with-borrowed-chords.png"
          caption="Roman numerals tell you the FUNCTION of a chord regardless of key. In any major scale: I ii iii IV V vi vii°. Borrowed chords (from the parallel minor) get a flat sign: ♭III ♭VI ♭VII. So in C major, the famous 'borrowed' progression I–♭VII–♭VI–V = C–B♭–A♭–G."
        />
      </Chapter>

      <Chapter eyebrow="The two-rule alphabet" title="Capitals for major thirds, smalls for minor">
        <TheoryBox label="The core rule">
          <div className="space-y-1 text-[14px]">
            <div><strong>Capital Roman</strong> &nbsp;→&nbsp; the chord contains a <em>major third</em>. Major chords. Augmented chords.</div>
            <div><strong>Lower-case Roman</strong> &nbsp;→&nbsp; the chord contains a <em>minor third</em>. Minor chords. Diminished chords.</div>
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            That single distinction — capital vs small — is the whole alphabet. Quality
            modifiers (°, +, 7, etc.) are appended after the numeral.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            That gives us seven possible numerals — I, ii, iii, IV, V, vi, vii — each in
            a major or minor flavour. Apply the rule to a major scale and you get the
            most-quoted formula in pop:
          </p>
        </Prose>

        <TheoryBox label="The major scale, in Roman numerals">
          <div className="font-mono text-[14px] text-amber-200">
            I &nbsp;·&nbsp; ii &nbsp;·&nbsp; iii &nbsp;·&nbsp; IV &nbsp;·&nbsp; V &nbsp;·&nbsp; vi &nbsp;·&nbsp; vii°
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            In C major: C, Dm, Em, F, G, Am, B°. The pattern (Major-minor-minor-Major-Major-minor-diminished)
            holds in every major key.
          </div>
        </TheoryBox>

        <ChordTree tonic="C" mode="major" />
      </Chapter>

      <Chapter eyebrow="Quality modifiers" title="°, +, and the seven extensions">
        <Prose>
          <p>
            Once you've got the numeral, append the chord quality the same way you would
            on a chart:
          </p>
        </Prose>

        <TheoryBox label="Quality modifiers">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>III+</strong> &nbsp;→&nbsp; augmented — always capital, because an augmented chord has a major 3rd</div>
            <div><strong>vii°</strong> &nbsp;→&nbsp; diminished (small because dim has a minor 3rd)</div>
            <div><strong>V7</strong> &nbsp;→&nbsp; dominant 7th built on the 5th degree</div>
            <div><strong>IVmaj7</strong> &nbsp;or&nbsp; <strong>IV△</strong> &nbsp;→&nbsp; major 7th on the IV</div>
            <div><strong>iiø</strong> &nbsp;or&nbsp; <strong>ii°7</strong> &nbsp;→&nbsp; half / fully diminished on the ii</div>
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Chromatic borrowings" title="When the chord is outside the key">
        <Prose>
          <p>
            Pop and film music constantly steal chords from outside the diatonic seven.
            Roman numerals handle this with a <strong>flat or sharp</strong> in front of
            the numeral, telling you the <em>root</em> has been altered relative to the
            scale.
          </p>
        </Prose>

        <Steps>
          <Step><strong>♭II</strong> — D♭ major in the key of C. Often used as a passing chord that resolves down a half-step to the tonic ("Neapolitan").</Step>
          <Step><strong>♭III</strong> — E♭ major in C. The home of the parallel minor's 3rd. Capital because the chord is major.</Step>
          <Step><strong>iv</strong> in a major key — F minor in C. Borrowed from the parallel minor; gives a melancholy colour.</Step>
          <Step><strong>♭VI</strong> — A♭ major in C. Borrowed from the parallel minor; the big cinematic lift under a held melody note.</Step>
          <Step><strong>♭VII</strong> — B♭ major in C. Mixolydian flavour. <strong>♭VII → IV → I</strong> is the classic rock cadence.</Step>
          <Step><strong>♯IV</strong> &nbsp;or&nbsp; <strong>♭V</strong> — F♯ in C. The tritone. Used as a passing chord or a tritone substitution.</Step>
        </Steps>
      </Chapter>

      <Chapter eyebrow="The minor scale" title="Roman numerals from the minor side">
        <Prose>
          <p>
            On the natural minor scale, the same letter-quality logic gives a different
            stack of numerals. Take A natural minor:
          </p>
        </Prose>

        <TheoryBox label="The minor scale, in Roman numerals">
          <div className="font-mono text-[14px] text-amber-200">
            i &nbsp;·&nbsp; ii° &nbsp;·&nbsp; ♭III &nbsp;·&nbsp; iv &nbsp;·&nbsp; v &nbsp;·&nbsp; ♭VI &nbsp;·&nbsp; ♭VII
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            In A minor: Am, B°, C, Dm, Em, F, G. The flats on III, VI and VII are
            <em> automatic</em> in natural minor — they remind you the scale itself
            differs from the parallel major.
          </div>
        </TheoryBox>

        <ChordTree tonic="A" mode="minor" />

        <Prose>
          <p>
            <strong>Harmonic minor</strong> raises the 7, which converts the v into a V
            (major dominant) and adds a vii° on the leading tone. So a minor-key song
            often switches between v (modal, sad) and V (functional, dramatic) depending
            on which scale flavour the melody calls for at that moment.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Secondary dominants" title="The 'V of' notation">
        <Prose>
          <p>
            A <strong>secondary dominant</strong> is a chord that acts as the V of a chord
            other than the I. In the key of C major, an <span className="font-mono">A7</span>
            does not belong diatonically — but A is the V of D, so A7 sets up D minor (the
            ii) like a personal dominant of its own. We notate this as
            <strong> V/ii</strong> — read aloud as "five of two."
          </p>
        </Prose>

        <TheoryBox label="The secondary dominants of C major">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>V/ii</strong> &nbsp;=&nbsp; A7 &nbsp;→&nbsp; Dm</div>
            <div><strong>V/iii</strong> &nbsp;=&nbsp; B7 &nbsp;→&nbsp; Em</div>
            <div><strong>V/IV</strong> &nbsp;=&nbsp; C7 &nbsp;→&nbsp; F &nbsp;<span className="text-nsm-dim">(the I7 is the V/IV)</span></div>
            <div><strong>V/V</strong> &nbsp;=&nbsp; D7 &nbsp;→&nbsp; G &nbsp;<span className="text-nsm-dim">(read "five of five" — the major II that sets up the V)</span></div>
            <div><strong>V/vi</strong> &nbsp;=&nbsp; E7 &nbsp;→&nbsp; Am</div>
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Note: there's no V/vii in major because the vii is diminished — too unstable
            to act as a temporary tonic.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            In day-to-day shorthand, "5 of 2" or "5 of 5" is what you'll hear in studios.
            Some analysts write the slash with an Arabic 5 above a Roman target — "5/V"
            — to make the temporary nature of the dominant explicit.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Inversions" title="Slashes for the bass">
        <Prose>
          <p>
            Roman numerals can carry the same slash-bass notation as chord charts — and
            the bass is named with respect to the <em>chord</em>, not the key. So
            <strong> IV/3</strong> means "the IV chord with its 3rd in the bass" — in C
            major, F major over A (F/A). <strong>IV/5</strong> would be F over C.
          </p>
          <p>
            That convention beats the alternative — <strong>IV/A</strong> — because the
            slash now translates with the rest of the chart. Move the song to G major
            and IV/3 automatically becomes C major over E. Move it to E♭ and IV/3 is A♭
            over C. The slash never breaks.
          </p>
        </Prose>

        <Glossary>
          <Term name="Roman numeral" def="A chord's name relative to the key. Capital for major-third chords, lowercase for minor-third chords." />
          <Term name="Borrowed chord" def="A chord from outside the key, named with ♭ or ♯ in front of its numeral (♭III, ♭VI, ♭VII, ♯IV)." />
          <Term name="Secondary dominant" def="A chord acting as the V of something other than the I. Written V/ii, V/iii, V/V, etc." />
          <Term name="Modal mixture" def="Borrowing chords from the parallel minor in a major key — iv, ♭III, ♭VI, ♭VII." />
          <Term name="Inversion (slash)" def="A chord with a chord-tone in the bass other than the root. IV/3 = IV with its 3rd in the bass." />
        </Glossary>

        <MTQuote>
          If you want to learn songs and if you want to understand how they work, Roman
          numerals comes into play. It's almost the only way to analyse a song harmonically.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/31b-roman-with-secondary-dominants-and-slash.png"
          caption="The complete RNA toolkit: natural minor (i ii° ♭III iv v ♭VI ♭VII), major (I ii iii IV V vi vii°), secondary dominants (V/ii, V/iii, V/IV, V/V, V/vi — temporary leading tones into a non-tonic chord), and slash chords for inversions (I/3, I/5, I/2, I/♭6)."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-32"
        label="Chapter 32 · How to Harmonise the Minor Scale"
        why="Most theory books harmonise major and stop. Now we turn to the minor — natural, harmonic, and Dorian — and discover an even richer chord vocabulary, including the augmented III♭ and the major V that drives every cinematic cadence."
      />
    </LessonShell>
  );
}
